<template>
    <div class="v-application vuetify-wrapper d-block mt-4" data-test="custom-invoices">
        <v-progress-linear v-if="loading" indeterminate />
        <v-alert v-if="error && !dialog" type="error" outlined data-test="custom-invoice-error">{{ error }}</v-alert>
        <v-alert v-if="context.create_block_reason" type="warning" outlined data-test="custom-invoice-block-reason">{{ context.create_block_reason.message }}</v-alert>
        <v-btn color="primary" class="mb-4" :disabled="loading || busy || !context.recipient || Boolean(context.create_block_reason)" data-test="custom-invoice-create" @click="open(null, false)">Individuelle Rechnung erstellen</v-btn>
        <v-simple-table>
            <thead><tr><th>Status</th><th>Referenz</th><th>Rechnungsdatum</th><th>Netto</th><th>MwSt.</th><th>Brutto</th><th>Aktionen</th></tr></thead>
            <tbody>
                <tr v-for="invoice in context.invoices" :key="invoice.id" data-test="custom-invoice-row" :data-invoice-id="invoice.id">
                    <td>{{ statusLabels[invoice.status] }}</td><td>{{ invoice.reference }}</td><td>{{ invoice.invoice_date.split('-').reverse().join('.') }}</td>
                    <td>{{ money(invoice.net_amount) }}</td><td>{{ money(invoice.vat_amount) }}</td><td>{{ money(invoice.gross_amount) }}</td>
                    <td>
                        <v-btn text small :disabled="busy" data-test="custom-invoice-view" @click="open(invoice, true)">Ansehen</v-btn>
                        <v-btn v-if="invoice.can_edit" text small :disabled="busy" data-test="custom-invoice-edit" @click="open(invoice, false)">Bearbeiten</v-btn>
                        <v-btn v-if="invoice.can_delete" text small color="error" :disabled="busy" data-test="custom-invoice-delete" @click="selected = invoice; deleteDialog = true">Löschen</v-btn>
                        <v-btn v-if="invoice.can_transfer" text small :disabled="busy" data-test="custom-invoice-transfer" @click="transition(invoice, 'transfer')">An Apobilanz übertragen</v-btn>
                        <v-btn v-if="invoice.can_retrieve" text small :disabled="busy" data-test="custom-invoice-retrieve" @click="transition(invoice, 'retrieve')">Aus Apobilanz zurückholen</v-btn>
                    </td>
                </tr>
                <tr v-if="!loading && context.invoices.length === 0"><td colspan="7">Noch keine individuellen Rechnungen.</td></tr>
            </tbody>
        </v-simple-table>
        <v-dialog v-model="dialog" persistent scrollable max-width="850" content-class="v-application">
            <custom-invoice-form v-if="dialog" :invoice="selected" :recipient="selected ? selected.recipient : context.recipient" :readonly="readonly" :busy="busy" :error="error" @close="dialog = false" @save="save" />
        </v-dialog>
        <delete-confirmation-dialog v-model="deleteDialog" message="Diesen Rechnungsentwurf löschen? Die Referenz bleibt dauerhaft reserviert." @confirm-deletion="remove" />
    </div>
</template>

<script>
import axios from 'axios';
import CustomInvoiceForm from './CustomInvoiceForm.vue';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog.vue';

export default {
    components: { CustomInvoiceForm, DeleteConfirmationDialog },
    props: {
        recipientType: { type: String, required: true },
        recipientId: { type: String, required: true },
    },
    data: () => ({
        context: { recipient: null, create_block_reason: null, invoices: [] },
        loading: false, busy: false, error: '', dialog: false, deleteDialog: false, selected: null, readonly: false,
        statusLabels: { draft: 'Entwurf', transferred: 'In Apobilanz', cancelled: 'Storniert' },
    }),
    created() {
        this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                const response = await axios.get(`/api/custom_invoices/${this.recipientType}/${this.recipientId}`);
                this.context = response.data;
            } catch (error) {
                this.error = error.response?.data?.message || 'Rechnungen konnten nicht geladen werden.';
            } finally {
                this.loading = false;
            }
        },
        open(invoice, readonly) {
            this.selected = invoice;
            this.readonly = readonly;
            this.error = '';
            this.dialog = true;
        },
        async save(form) {
            if (this.busy) return;
            this.busy = true;
            this.error = '';
            try {
                if (this.selected) {
                    await axios.put(`/api/custom_invoices/${this.selected.id}`, { ...form, version: this.selected.version });
                } else {
                    await axios.post(`/api/custom_invoices/${this.recipientType}/${this.recipientId}`, form);
                }
                this.dialog = false;
                await this.load();
            } catch (error) {
                this.error = error.response?.data?.message || 'Rechnung konnte nicht gespeichert werden.';
                if (error.response?.status === 409) {
                    this.dialog = false;
                    this.selected = null;
                    this.error = 'Die Rechnung wurde inzwischen geändert. Bitte zum Bearbeiten erneut öffnen.';
                    await this.load();
                }
            } finally {
                this.busy = false;
            }
        },
        async transition(invoice, action) {
            if (this.busy) return;
            this.busy = true;
            this.error = '';
            try {
                await axios.post(`/api/custom_invoices/${invoice.id}/${action}`, { version: invoice.version });
                await this.load();
            } catch (error) {
                this.error = error.response?.data?.message || 'Aktion konnte nicht ausgeführt werden.';
                await this.load();
            } finally {
                this.busy = false;
            }
        },
        async remove() {
            if (this.busy) return;
            this.busy = true;
            this.error = '';
            try {
                await axios.delete(`/api/custom_invoices/${this.selected.id}`, { data: { version: this.selected.version } });
                await this.load();
            } catch (error) {
                this.error = error.response?.data?.message || 'Entwurf konnte nicht gelöscht werden.';
                if (error.response?.status === 409) {
                    this.deleteDialog = false;
                    this.selected = null;
                    this.error = 'Die Rechnung wurde inzwischen geändert. Bitte vor dem Löschen erneut prüfen.';
                    await this.load();
                }
            } finally {
                this.busy = false;
            }
        },
        money(value) {
            return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
        },
    },
};
</script>
